import { useCallback, useMemo } from 'react';
import classnames from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { testId } from 'ui-components/services/testid-service';

import { messages } from './messages';
import style from './style.module.css';
import { ModalPortal } from '../../components/modal-portal';
import { ErrorModal } from '../../components/modals/error-modal';
import { CostReviewModalContainer } from '../../containers/cost-review-modal';
import { useModal } from '../../hooks/use-modal';
import { clearCostReview } from '../../store/bulk/bulk.actions';
import { costReviewModalClose, costReviewModalOpen } from '../../store/bulk/cost-review/bulk.cost-review.actions';
import { selectBulkCostReview, selectBulkJobByJobGuid } from '../../store/bulk/bulk.selectors';

/**
 * @param {object} props
 * @param {string} [props.className]
 * @param {string[]} props.fulfillmentPlanIds
 * @param {boolean} [props.linkDisabled] - Consider using the `getShouldDisableCostReview` helper function to determine this value
 * @param {string} [props.buttonClassName]
 * @returns {React.JSX.Element}
 */
export const CostReviewLink = ({ className = null, fulfillmentPlanIds, linkDisabled = false, buttonClassName }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const { isOpen: isCostReviewModalOpen, open: openCostReviewModal, close: closeCostReviewModal } = useModal();

  const costReviewJobGuid = useSelector((state) => {
    const { jobGuid } = selectBulkCostReview(state) || {};
    return jobGuid;
  });

  const costReviewErrorMessage = useSelector((state) => {
    const { jobGuid, hasError } = selectBulkCostReview(state) || {};

    if (hasError) {
      return formatMessage(messages.unknownErrorWithoutMessage);
    }

    if (jobGuid) {
      const costReviewJob = selectBulkJobByJobGuid(state, { jobGuid });
      return costReviewJob?.costReviewData?.errorMessage;
    }
  });

  const onOpenCostReview = useCallback(() => {
    dispatch(costReviewModalOpen(fulfillmentPlanIds));
    openCostReviewModal();
  }, [openCostReviewModal, dispatch, fulfillmentPlanIds]);

  const onCloseCostReview = useCallback(() => {
    dispatch(clearCostReview(costReviewJobGuid));
    dispatch(costReviewModalClose());
    closeCostReviewModal();
  }, [closeCostReviewModal, costReviewJobGuid, dispatch]);

  const modalFrameworkMethods = useMemo(() => {
    return {
      closeModal: onCloseCostReview,
    };
  }, [onCloseCostReview]);

  return (
    <div className={classnames(style['cost-review-link'], className)} {...testId('cost-review-link')}>
      <Button className={buttonClassName} disabled={linkDisabled} onClick={onOpenCostReview} theme='link'>
        <FormattedMessage {...messages.costReviewButton} />
      </Button>
      {isCostReviewModalOpen && !costReviewErrorMessage ? (
        <ModalPortal onClose={onCloseCostReview}>
          <CostReviewModalContainer
            fulfillmentPlanIds={fulfillmentPlanIds}
            isReloadCostReviewDisabled={linkDisabled}
            jobGuid={costReviewJobGuid}
            modalFrameworkMethods={modalFrameworkMethods}
            readOnlyCostReviewModal
          />
        </ModalPortal>
      ) : null}
      {isCostReviewModalOpen && costReviewErrorMessage ? (
        <ModalPortal onClose={onCloseCostReview}>
          <ErrorModal
            message={<FormattedMessage {...messages.genericCostReviewErrorMessage} values={{ errorMessage: costReviewErrorMessage }} />}
            modalFrameworkMethods={modalFrameworkMethods}
            title={<FormattedMessage {...messages.genericCostReviewErrorMessageTitle} />}
          />
        </ModalPortal>
      ) : null}
    </div>
  );
};
